import { Component, inject, OnDestroy, TemplateRef } from '@angular/core';

//SUB COMPONENTS
import { HeaderComponent } from '@components/dashboard/header/header.component';
import { NavComponent } from '@components/dashboard/navigation/nav.component';
import { DashboardService } from '@services/dashboard.service';
import { Rider } from '@interfaces/models.interface';
import { CommonModule } from '@angular/common';
import { LocalService } from '@services/local.service';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RouterModule } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { AuthStore } from '@store/auth.store';
import { Router } from '@angular/router';
import { BaseCoreUtils } from '@core/base-core-util';
import { ModuleNames } from '@enums/module-names';
import { PARTNER_SUMMARY, partnersListQuery } from '@gql_queries/partners/queries';
import { TOPUP_MANUAL } from '@gql_queries/partners/mutation';
import {
    PARTNERS_DOWNLOAD_REPORT_V1,
    PARTNERS_DOWNLOAD_REPORT_V2,
} from '@gql_queries/report_download/queries';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { PartnerReportTemplate } from '@shared/utils/download-handler-util';
import { isUnsupportedByApi } from '@shared/utils/graphql-support.util';
import {
    PartnerServiceFilter,
    PartnerVehicleClassFilter,
    parsePartnerDate,
    partnerServiceTags,
    partnerStatusClass,
    partnerStatusLabel,
    partnerVehicleLabel,
} from '@shared/utils/partner-presentation.util';

/**
 * Status tabs, in the order the design lists them.
 *
 * These four stages cover every value `PartnerStatus` can hold, so they add up
 * to All. A new status added without a tab here would break that silently —
 * the backend's partner-summary spec asserts the coverage.
 */
const STATUS_TABS = [
    { key: 'FOR_ALL', label: 'All', count: 'totalRegistrations' },
    { key: 'APPROVED', label: 'Approved', count: 'approved' },
    { key: 'FOR_REVIEW', label: 'Under Review', count: 'underReview' },
    { key: 'INITIAL_REGISTERED', label: 'With Documents', count: 'withDocuments' },
    { key: 'FOR_AMMENDMENT', label: 'For Amendment', count: 'forAmendment' },
] as const;

@Component({
    selector: 'app-dashboard-riders',
    standalone: true,
    templateUrl: './dashboard-riders.component.html',
    styleUrl: './dashboard-riders.component.css',
    imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule, NgxSpinnerModule],
})

export class DashboardRidersComponent implements OnDestroy {
    private coreUtils!: BaseCoreUtils;
    private modalService = inject(NgbModal);

    constructor(
        private router: Router,
        private dasher: DashboardService,
        private localservice: LocalService,
        private apollo: Apollo,
        private authStore: AuthStore,
        private toastr: ToastrService,
        private spinner: NgxSpinnerService
    ) {
        this.coreUtils = new BaseCoreUtils({
            module: ModuleNames.PARTNERS,
            apollo: this.apollo,
            router: this.router,
            authStore: this.authStore,
        });
    }

    public riderList: Rider[] = [];
    public isLoadingList: boolean = true;
    public searchKeyword: string = '';
    public partnerReportDate: string = this.formatDateForInput(new Date());
    public isDownloadingReport = false;
    private spinnerShownAt = 0;
    private readonly minSpinnerDurationMs = 800;
    public currentSelectedPageList: Rider[] = [];
    public maxPages: number = 0;
    public disableActionBtns: any = {
        next: false,
        prev: false,
    };
    public riderPages: number[] = [];
    public currentPage: number = 1;
    public pageSize: number = 2;

    public readonly statusTabs = STATUS_TABS;


    public partnerTabs: Record<string, any> = {
        FOR_ALL: true,
        APPROVED: false,
        FOR_REVIEW: false,
        FOR_AMMENDMENT: false,
        INITIAL_REGISTERED: false,
    }

    /** The five numbers beside the status tabs; shared with the Summary tab. */
    public pipeline: Record<string, number> = {
        totalRegistrations: 0,
        underReview: 0,
        withDocuments: 0,
        forAmendment: 0,
        approved: 0,
    };

    /** Counts are taken as of this date; it also drives the Summary tab. */
    public asOf: string = this.formatDateForInput(new Date());

    /** Null until the counts load — a 0 beside a tab that failed is a lie. */
    public pipelineLoaded = false;

    /**
     * Set when the API predates part of this page. The filters and the tab
     * counts need a backend release; the list itself does not, so it keeps
     * working and the page says which half is missing.
     */
    public apiMissingFilters = false;
    public apiMissingCounts = false;

    public vehicleFilter: PartnerVehicleClassFilter | '' = '';
    public serviceFilter: PartnerServiceFilter | '' = '';

    /** "<field>:<direction>", so one <select> carries both. */
    public sortChoice: string = 'dateCreated:desc';

    topUpForm: Record<any, any> = {
        amt: 0,
        partnerId: '',
    }

    private searchSubject = new Subject<string>();
    private searchSub!: Subscription;

    ngOnInit(): void {
        this.searchSub = this.searchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged(),
        ).subscribe(() => this.reload());

        this.reload();
        this.loadPipeline();
    }

    /** Current tab key, defaulting to All. */
    private activeTab(): string {
        return Object.keys(this.partnerTabs).find(x => this.partnerTabs[x]) || 'FOR_ALL';
    }

    /** Reset to the first page and fetch with every filter applied. */
    private reload(): void {
        this.currentPage = 1;
        this.maxPages = 0;
        this.riderPages = [];
        this.disableActionBtns = { next: false, prev: false };
        this.fetchPartnersByStatus(this.activeTab());
    }

    openPartnerReportModal(reportModal: TemplateRef<unknown>): void {
        this.modalService.open(reportModal, {
            size: 'md',
            backdrop: 'static',
            keyboard: false
        });
    }

    downloadPartnerReport(template: PartnerReportTemplate): void {
        if (this.isDownloadingReport) {
            return;
        }

        const reportConfig: Record<PartnerReportTemplate, { query: any; dataField: string; label: string }> = {
            template_1: {
                query: PARTNERS_DOWNLOAD_REPORT_V1,
                dataField: 'partnersDownloadReportV1',
                label: 'Partner Report Template 1',
            },
            template_2: {
                query: PARTNERS_DOWNLOAD_REPORT_V2,
                dataField: 'partnersDownloadReportV2',
                label: 'Partner Report Template 2',
            },
        };

        const selectedConfig = reportConfig[template];
        if (!selectedConfig) {
            return;
        }

        this.isDownloadingReport = true;
        this.spinnerShownAt = Date.now();
        this.spinner.show();

        this.apollo.query({
            fetchPolicy: 'network-only',
            query: selectedConfig.query,
            variables: {},
        }).pipe(
            finalize(() => this.hideReportSpinner())
        ).subscribe({
            next: (result: any) => {
                const msg: string | undefined = result?.data?.[selectedConfig.dataField]?.message;

                if (msg) {
                    this.toastr.success(msg, selectedConfig.label, {
                        closeButton: true,
                        timeOut: 3000,
                    });
                    this.modalService.dismissAll();
                } else {
                    this.toastr.error('An error occured, please try again', 'UNKNWN_ERR', {
                        closeButton: true,
                        timeOut: 3000,
                    });
                }
            },
            error: (error) => {
                if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
                    this.authStore.logout().subscribe(() => {
                        this.router.navigate(['/admin/login']);
                    });
                    return;
                }

                this.toastr.error(error.message || 'Unable to download report', error.code || 'ERR', {
                    closeButton: true,
                    timeOut: 3000,
                });
            },
        });
    }

    private hideReportSpinner(): void {
        const elapsed = Date.now() - this.spinnerShownAt;
        const remainingDelay = Math.max(0, this.minSpinnerDurationMs - elapsed);

        setTimeout(() => {
            this.isDownloadingReport = false;
            this.spinner.hide();
        }, remainingDelay);
    }

    formatDateForInput(date: Date): string {
        return date.toISOString().slice(0, 10);
    }

    fetchPartnersByStatus(status:string = 'FOR_ALL', action: string = '', searchQuery?: string) {
        var fromPage = 1;

        if(action != ''){
            fromPage = this.currentPage;
            switch(action){
                case 'next':
                    this.currentPage++
                break;

                case 'prev':
                    if(this.currentPage != 1){
                        this.currentPage--
                    }//end if
                break;
            }//end swithc
        }//end if

        this.isLoadingList = true;

        const [sortBy, sortDirection] = this.sortChoice.split(':');

        // Only asks for the vehicle/service filters when one is selected, so the
        // page still lists partners against an API that does not have them.
        const query = partnersListQuery({
            vehicleClass: this.vehicleFilter,
            service: this.serviceFilter,
        });

        this.coreUtils.fetchList(query, {
            page: this.currentPage,
            status: status,
            searchQuery: searchQuery ?? this.searchKeyword,
            sortBy,
            sortDirection,
            // Omitted rather than sent as null: "all vehicles" and "all
            // services" mean no filter at all.
            vehicleClass: this.vehicleFilter || undefined,
            service: this.serviceFilter || undefined,
            // "As of" bounds the rows the same way it bounds the tab counts, so
            // the two can't tell different stories on one screen.
            end: this.asOf || undefined,
        }, (data) => {
            this.isLoadingList = false;
            const partners: Rider[] = data['partners'] ?? [];

            if (partners.length != 0 || action === '') {
                this.riderList = partners;
                this.currentSelectedPageList = partners;
                // A short page is the last page.
                this.disableActionBtns.next = partners.length < 25;
            } else {
                // Paged past the end — stay where we were and stop offering Next.
                this.currentPage = fromPage;
                this.disableActionBtns.next = true;
            }
        }, (error) => {
            this.isLoadingList = false;
            this.riderList = [];
            this.currentSelectedPageList = [];
            this.currentPage = fromPage;

            // An API without the filters rejects the whole document, so drop
            // back to the unfiltered list rather than showing an empty table.
            if (isUnsupportedByApi(error) && (this.vehicleFilter || this.serviceFilter)) {
                this.apiMissingFilters = true;
                this.vehicleFilter = '';
                this.serviceFilter = '';
                this.fetchPartnersByStatus(status, '', searchQuery);
            }
        });
    }//end fn

    /** The tab counts and the Summary cards come from the same query. */
    loadPipeline(){
        this.coreUtils.overview(PARTNER_SUMMARY, { asOf: this.asOf }, (data) => {
            const summary = data?.['partnerSummary'];
            if (summary?.pipeline) {
                Object.keys(this.pipeline).forEach((key) => {
                    this.pipeline[key] = summary.pipeline[key] ?? 0;
                });
                this.pipelineLoaded = true;
                this.apiMissingCounts = false;
            }
        }, (error) => {
            // Leave the chips off rather than showing 0 for counts that never
            // arrived — zero is a real answer and would be read as one.
            this.pipelineLoaded = false;
            this.apiMissingCounts = isUnsupportedByApi(error);

            // The counts and the filters ship in the same release, so an API
            // without partnerSummary has no filters either. Disable them up
            // front instead of letting the first pick fail.
            if (this.apiMissingCounts) {
                this.apiMissingFilters = true;
                this.vehicleFilter = '';
                this.serviceFilter = '';
            }
        });
    }//end fn

    tabCount(tabKey: string): number {
        const tab = STATUS_TABS.find((entry) => entry.key === tabKey);
        return tab ? this.pipeline[tab.count] ?? 0 : 0;
    }

    onAsOfChange(){
        if (!this.asOf) return;
        this.loadPipeline();
        this.reload();
    }

    onFilterChange(){
        this.reload();
    }

    nextPrev(action: string){
        this.fetchPartnersByStatus(this.activeTab(), action);
    }//end fn

    searchData(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.searchKeyword = inputElement.value;
        this.searchSubject.next(this.searchKeyword);
    }//end nfn

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
    }

    viewDetails(details: Rider){
        this.router.navigate(['/admin/riders', details.partnerId]);
    }//end fn

    filterUser(tabindex: string){
        this.riderList = [];
        this.currentSelectedPageList = [];

        Object.keys(this.partnerTabs).forEach((x) => {
            this.partnerTabs[x] = false;
        });

        this.partnerTabs[tabindex] = true;

        this.reload();
    }//end

    onSortChange(){
        this.reload();
    }

    /* ---- row presentation ------------------------------------------------ */

    fullName(rider: Rider): string {
        return [rider.firstName, rider.middleName, rider.lastName]
            .filter((part) => !!part && part.trim() !== '')
            .join(' ');
    }

    vehicleLabel(rider: Rider): string {
        return partnerVehicleLabel(rider);
    }

    serviceTags(rider: Rider): { label: string; className: string }[] {
        // Only an approved partner has services to show; everyone else is still
        // being reviewed, and listing their requested services would read as
        // though they had been granted.
        return rider.status === 'approved' ? partnerServiceTags(rider) : [];
    }

    approvedOn(rider: Rider): string {
        const date = parsePartnerDate(rider.dateApprove);
        if (!date) return '—';
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    }

    statusLabel(status: string): string {
        return partnerStatusLabel(status);
    }

    statusClass(status: string): string {
        return partnerStatusClass(status);
    }

    /* ---- manual top-up (existing capability, kept) ----------------------- */

    manualTopUp(partnerId: string, modal: TemplateRef<any>){
        this.topUpForm['partnerId'] = partnerId;
        this.modalService.open(modal, { size: 'md',  backdrop: 'static'});
    }//end fn

    confirmManualTopUp(modal: TemplateRef<any>){
        this.modalService.open(modal, { size: 'md',  backdrop: 'static'});
    }//end fn

    processTopUp(successModal: TemplateRef<any>){
        this.coreUtils.update(TOPUP_MANUAL, {
            partnerId: this.topUpForm['partnerId'],
            amount: parseFloat(this.topUpForm['amt'])
        }, (data) => {
            if(data['topUpManual'] != null){
                this.topUpForm['amt'] = 0;
                this.topUpForm['partnerId'] = '';
                this.modalService.open(successModal, { size: 'md',  backdrop: 'static', keyboard: false });
            }
        });
    }//end processing

    closeAllModals(){
        this.modalService.dismissAll();
    }//end fn

}
